import type { PhotoQuarterTurn } from '@cer-autofill/shared';

export const PHOTO_MAX_BYTES = 1_000_000;
const MAX_EDGE_PX = 1920;
const JPEG_QUALITIES = [0.86, 0.76, 0.64, 0.52] as const;
/** #151515, the field stamp's footer fill, plus a margin for JPEG rounding. */
const FOOTER_RGB = 21;
const FOOTER_TOLERANCE = 28;

export interface PreparedPhoto {
  file: File;
  /** Pixel height of the evidence strip appended under the photo. */
  evidenceFooterPx: number;
}

/**
 * Field stamp geometry for a canvas of width W.
 * fontSize = max(18, round(W/55)), padding = round(fontSize*0.6),
 * lineHeight = round(fontSize*1.4), footer = 2*lineHeight + 2*padding.
 */
export function evidenceFooterGeometry(width: number): {
  fontSize: number;
  padding: number;
  lineHeight: number;
  footer: number;
} {
  if (!Number.isFinite(width) || width <= 0) throw new Error('Image has invalid dimensions.');
  const fontSize = Math.max(18, Math.round(width / 55));
  const padding = Math.round(fontSize * 0.6);
  const lineHeight = Math.round(fontSize * 1.4);
  return { fontSize, padding, lineHeight, footer: 2 * lineHeight + 2 * padding };
}
export interface PhotoStamp {
  jobId: string;
  capturedAt: string;
  utcOffsetMinutes: number;
  timeSource: 'camera' | 'recorded';
  jobNumber?: string;
}

export function scaledDimensions(
  width: number,
  height: number,
  maxEdge = MAX_EDGE_PX,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
    throw new Error('Image has invalid dimensions.');
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function photoStampLines(stamp: PhotoStamp): [string, string] {
  const time = Date.parse(stamp.capturedAt);
  if (
    !Number.isFinite(time) ||
    !Number.isInteger(stamp.utcOffsetMinutes) ||
    Math.abs(stamp.utcOffsetMinutes) > 840 ||
    !stamp.jobId.trim()
  )
    throw new Error('Photo stamp needs a valid job and time.');
  const local = new Date(time + stamp.utcOffsetMinutes * 60_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const offset = Math.abs(stamp.utcOffsetMinutes);
  const zone = `UTC${stamp.utcOffsetMinutes >= 0 ? '+' : '-'}${pad(Math.floor(offset / 60))}:${pad(offset % 60)}`;
  return [
    `${stamp.timeSource === 'camera' ? 'Captured' : 'Recorded'}: ${pad(local.getUTCDate())}/${pad(local.getUTCMonth() + 1)}/${local.getUTCFullYear()} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())} ${zone}`,
    `Report: ${stamp.jobId.trim()}${stamp.jobNumber ? ` | Job: ${stamp.jobNumber.trim()}` : ''}`,
  ];
}

/** Append an evidence footer without covering the original image; never return 1 MB or more. */
export async function preparePhotoForUpload(file: File, stamp: PhotoStamp): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file.');
  const lines = photoStampLines(stamp);
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  for (const maxEdge of [1920, 1536, 1228, 982, 786, 628]) {
    const dimensions = scaledDimensions(image.naturalWidth, image.naturalHeight, maxEdge);
    canvas.width = Math.max(640, dimensions.width);
    const { fontSize, padding, lineHeight, footer } = evidenceFooterGeometry(canvas.width);
    canvas.height = dimensions.height + footer;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This device cannot prepare the photo for upload.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      image,
      Math.floor((canvas.width - dimensions.width) / 2),
      0,
      dimensions.width,
      dimensions.height,
    );
    context.fillStyle = '#151515';
    context.fillRect(0, dimensions.height, canvas.width, canvas.height - dimensions.height);
    context.fillStyle = '#ffffff';
    context.textBaseline = 'top';
    for (const [index, line] of lines.entries()) {
      let size = fontSize;
      context.font = `600 ${size}px sans-serif`;
      while (context.measureText(line).width > canvas.width - 2 * padding && size > 10) {
        context.font = `600 ${--size}px sans-serif`;
      }
      if (context.measureText(line).width > canvas.width - 2 * padding)
        throw new Error('Photo stamp is too long to fit. Check the job number.');
      context.fillText(line, padding, dimensions.height + padding + index * lineHeight);
    }
    for (const quality of JPEG_QUALITIES) {
      const prepared = await canvasToBlob(canvas, quality);
      if (prepared.size < PHOTO_MAX_BYTES) {
        const basename = file.name.replace(/\.[^.]+$/, '') || 'inspection-photo';
        return {
          file: new File([prepared], `${basename}.jpg`, {
            type: 'image/jpeg',
            lastModified: file.lastModified,
          }),
          evidenceFooterPx: footer,
        };
      }
    }
  }
  throw new Error('Photo could not be reduced below 1 MB. Try a closer, lower-resolution photo.');
}

/**
 * Re-encode a JPEG with a quarter turn baked in, keeping a known or detected
 * evidence strip at the bottom. Output is under 1 MB and carries no EXIF
 * orientation (canvas JPEG does not write one).
 */
export async function rotatePreparedPhoto(
  blob: Blob,
  degrees: PhotoQuarterTurn,
  footerPx?: number,
): Promise<{ blob: Blob; evidenceFooterPx?: number }> {
  const image = await loadImage(blob);
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  const source = document.createElement('canvas');
  source.width = width;
  source.height = height;
  const sourceContext = source.getContext('2d');
  if (!sourceContext) throw new Error('This device cannot rotate the photo.');
  sourceContext.drawImage(image, 0, 0);
  const footer = resolveFooterHeight(sourceContext, width, height, footerPx);
  const composed = composeRotated(image, width, height, degrees, footer);
  const encoded = await encodeCanvasUnderCap(composed);
  const scale = composed.height === 0 ? 1 : encoded.canvas.height / composed.height;
  const scaledFooter = footer !== undefined ? Math.round(footer * scale) : undefined;
  return {
    blob: encoded.blob,
    ...(scaledFooter !== undefined && scaledFooter > 0 ? { evidenceFooterPx: scaledFooter } : {}),
  };
}

function resolveFooterHeight(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  footerPx: number | undefined,
): number | undefined {
  if (footerPx !== undefined && Number.isInteger(footerPx) && footerPx > 0 && footerPx < height) {
    return footerPx;
  }
  return detectFooterPx(context, width, height);
}

function detectFooterPx(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): number | undefined {
  let geometry: ReturnType<typeof evidenceFooterGeometry>;
  try {
    geometry = evidenceFooterGeometry(width);
  } catch {
    return undefined;
  }
  const { footer, padding } = geometry;
  if (padding < 1 || footer >= height) return undefined;
  let pixels: Uint8ClampedArray;
  try {
    pixels = context.getImageData(0, 0, width, height).data;
  } catch {
    return undefined;
  }
  const bandTop = height - footer;
  let dark = 0;
  let total = 0;
  const sample = (x: number, y: number) => {
    total += 1;
    if (isFooterDark(pixels, width, x, y)) dark += 1;
  };
  const left = Math.min(padding, width);
  for (let y = bandTop; y < height; y++) {
    for (let x = 0; x < left; x++) sample(x, y);
    for (let x = Math.max(0, width - padding); x < width; x++) sample(x, y);
  }
  const topEnd = Math.min(height, bandTop + padding);
  for (let y = bandTop; y < topEnd; y++) {
    for (let x = 0; x < width; x++) sample(x, y);
  }
  for (let y = Math.max(bandTop, height - padding); y < height; y++) {
    for (let x = 0; x < width; x++) sample(x, y);
  }
  if (total === 0 || dark / total < 0.9) return undefined;
  const above = bandTop - 1;
  if (above < 0) return undefined;
  for (let x = 0; x < width; x++) {
    if (!isFooterDark(pixels, width, x, above)) return footer;
  }
  return undefined;
}

function isFooterDark(data: Uint8ClampedArray, width: number, x: number, y: number): boolean {
  const i = (y * width + x) * 4;
  const r = data[i] ?? 0;
  const g = data[i + 1] ?? 0;
  const b = data[i + 2] ?? 0;
  return (
    Math.abs(r - FOOTER_RGB) <= FOOTER_TOLERANCE &&
    Math.abs(g - FOOTER_RGB) <= FOOTER_TOLERANCE &&
    Math.abs(b - FOOTER_RGB) <= FOOTER_TOLERANCE &&
    Math.max(r, g, b) - Math.min(r, g, b) <= 16
  );
}

function composeRotated(
  image: CanvasImageSource,
  width: number,
  height: number,
  degrees: PhotoQuarterTurn,
  footer: number | undefined,
): HTMLCanvasElement {
  const footerPx = footer ?? 0;
  const imageHeight = height - footerPx;
  const swap = degrees === 90 || degrees === 270;
  const turnedWidth = swap ? imageHeight : width;
  const turnedHeight = swap ? width : imageHeight;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, turnedWidth);
  canvas.height = Math.max(1, turnedHeight + footerPx);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This device cannot rotate the photo.');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  drawTurned(context, image, width, imageHeight, degrees, turnedWidth, turnedHeight);
  if (footerPx > 0) {
    context.drawImage(
      image,
      0,
      imageHeight,
      width,
      footerPx,
      0,
      turnedHeight,
      turnedWidth,
      footerPx,
    );
  }
  return canvas;
}

function drawTurned(
  context: CanvasRenderingContext2D,
  image: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  degrees: PhotoQuarterTurn,
  destWidth: number,
  destHeight: number,
): void {
  context.save();
  if (degrees === 90) {
    context.translate(destWidth, 0);
    context.rotate(Math.PI / 2);
  } else if (degrees === 180) {
    context.translate(destWidth, destHeight);
    context.rotate(Math.PI);
  } else {
    context.translate(0, destHeight);
    context.rotate(-Math.PI / 2);
  }
  context.drawImage(image, 0, 0, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);
  context.restore();
}

async function encodeCanvasUnderCap(
  source: HTMLCanvasElement,
): Promise<{ blob: Blob; canvas: HTMLCanvasElement }> {
  let current = source;
  for (let attempt = 0; attempt < 8; attempt++) {
    for (const quality of JPEG_QUALITIES) {
      const prepared = await canvasToBlob(current, quality);
      if (prepared.size < PHOTO_MAX_BYTES) return { blob: prepared, canvas: current };
    }
    const edge = Math.max(current.width, current.height);
    const nextEdge = Math.floor(edge * 0.75);
    if (nextEdge < 64 || nextEdge >= edge) break;
    current = scaleCanvas(current, nextEdge);
  }
  throw new Error('Rotated photo could not be reduced below 1 MB.');
}

function scaleCanvas(source: HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const scale = maxEdge / Math.max(source.width, source.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This device cannot rotate the photo.');
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The selected photo could not be decoded.'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Photo conversion failed.'))),
      'image/jpeg',
      quality,
    );
  });
}
